import { AppContext } from '../contexts';
import { useEffect, useState } from 'react';
import { get } from '../api';
import { useAuth, useSnack } from '../hooks';
import { useTranslation } from 'react-i18next';
import { RoleEnum } from '@koob/enums';
import { actingOrganizationOf, isTransientError } from '@koob/utils';
import { KoobLoadError, KoobLoadingOverlay } from '@koob/ui';

const permissions = {
  inviteUser: [RoleEnum.TO_ADMIN],
  readSelfOrganization: [
    RoleEnum.TO_ADMIN,
    RoleEnum.TO_USER,
    RoleEnum.DMC_USER,
  ],
  manageSelfOrganization: [RoleEnum.TO_ADMIN],
  manageSelfOrganizationUsers: [RoleEnum.TO_ADMIN],
  readTravelDiaryContent: [RoleEnum.TO_ADMIN, RoleEnum.KOOB_ADMIN],
  manageTravelDiaryContent: [RoleEnum.TO_ADMIN, RoleEnum.KOOB_ADMIN],
  readAuditLog: [RoleEnum.TO_ADMIN, RoleEnum.KOOB_ADMIN],
};

const AppProvider = ({ children }) => {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const { notify } = useSnack();

  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoaded(false);
    setLoadError(null);
    get('/api/v1/user')
      .then(r => setUser(r.data))
      // An expired session reloads to the login screen (api interceptor).
      .catch(setLoadError)
      .finally(() => setLoaded(true));
  }, [attempt]);

  const currentUserRole = user?.role;

  if (
    currentUserRole &&
    (currentUserRole === RoleEnum.DMC_ADMIN ||
      currentUserRole === RoleEnum.DMC_USER)
  ) {
    logout();
    notify(t('errors:userNotAuthorized'), { type: 'error' });
  }

  const canManageUser = targetUser => {
    if (currentUserRole === RoleEnum.TO_ADMIN) return true;

    if (targetUser?.id === user?.id) {
      return false;
    }

    return false;
  };

  const isAllowedTo = name =>
    (permissions[name] || []).indexOf(currentUserRole) !== -1;

  // The organization this tab acts as (KD-3735 picker), else the first.
  const currentOrganization = actingOrganizationOf(user);
  const currentOrganizationPosition = {
    lat: parseFloat(currentOrganization?.lat),
    lon: parseFloat(currentOrganization?.lon),
  };

  // Until the user and their organizations load: Koob's loader, over the
  // whole screen. A failed request shows the app as before.
  if (!loaded) return <KoobLoadingOverlay />;

  // KD-3991: without its user the app renders blank pages; a busy API (429)
  // or one out of reach gets a way to try again.
  if (isTransientError(loadError)) {
    return (
      <KoobLoadError
        fullScreen
        onRetry={() => setAttempt(previous => previous + 1)}
      />
    );
  }

  return (
    <AppContext.Provider
      value={{
        user: user,
        currentUserRole,
        currentOrganizationId: currentOrganization?.id,
        currentOrganization,
        currentOrganizationIdInt: currentOrganization?.organizationId,
        currentUserLang: currentOrganization?.language,
        currentOrganizationPosition,
        canManageUser,
        isAllowedTo,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
